/**
 * Lobby Tools contract checks — runs in the in-game ContractDashboard
 * alongside every other domain check (abilities, hotbar, loot, mobs, etc.).
 *
 * Uses the shared check builders in `./helpers.ts` so each invariant is
 * a one-liner — the domain logic stays readable.
 */

import type { Check } from '../../DashboardShared';
import type { Category } from '../types';
import {
  LOBBY_MAP_IDS,
  LOBBY_TOOLS_MAP_REGISTRY,
  LOBBY_TOOL_BOT_CLASS,
  LOBBY_TOOL_COOLDOWN_MS,
  LOBBY_TOOL_DEFINITIONS,
  LOBBY_TOOL_IDS,
  LOBBY_TOOL_MAX_BOTS_PER_PLAYER,
  LOBBY_TOOL_MAX_MOBS_PER_PLAYER,
  LOBBY_TOOL_MESSAGE,
  LOBBY_TOOL_SPAWN_OFFSET_PX,
  LOBBY_TOOLS,
  getLobbyToolsMapConfig,
} from '../../../../../shared/constants/LobbyTools';
import { MOB_REGISTRY } from '../../../../../shared/MobRegistry';
import { DevConfig } from '../../../../../config/environment/devConfig';
import { LevelRegistry } from '../../../../systems/map/LevelRegistry';
import {
  booleanFlagCheck,
  capsSanityCheck,
  driftStatus,
  enumUniqueCheck,
  registryAgreementCheck,
  stringPatternCheck,
} from './helpers';

export function checkLobbyToolsContracts(): Category {
  const checks: Check[] = [];

  // 1. Registry is non-empty and agrees with the derived LOBBY_MAP_IDS.
  const registryIds = LOBBY_TOOLS_MAP_REGISTRY.filter(e => e.config.enabled).map(e => e.id);
  checks.push(registryAgreementCheck({
    id: 'lt_registry_derived',
    label: 'LOBBY_MAP_IDS ↔ registry',
    left: { name: 'registry', ids: registryIds },
    right: { name: 'derived', ids: LOBBY_MAP_IDS },
  }));

  // 2. Definitions align with LOBBY_TOOLS enum.
  checks.push(registryAgreementCheck({
    id: 'lt_defs_match_enum',
    label: 'LOBBY_TOOL_DEFINITIONS ↔ LOBBY_TOOLS',
    left: { name: 'defs', ids: LOBBY_TOOL_DEFINITIONS.map(d => d.id) },
    right: { name: 'enum', ids: LOBBY_TOOL_IDS },
  }));

  // 3. Every spawn_mob definition has mobType in MOB_REGISTRY.
  const spawnDefs = LOBBY_TOOL_DEFINITIONS.filter(d => d.kind === 'spawn_mob');
  const missingMobTypes = spawnDefs.filter(d => !d.mobType || !MOB_REGISTRY[d.mobType]);
  checks.push({
    id: 'lt_mob_types_valid',
    label: 'spawn_mob tools → MOB_REGISTRY',
    status: missingMobTypes.length === 0 ? 'pass' : 'fail',
    detail: missingMobTypes.length === 0
      ? `${spawnDefs.length}/${spawnDefs.length} resolve in MOB_REGISTRY`
      : `Missing: ${missingMobTypes.map(d => `${d.id}→${d.mobType ?? '∅'}`).join(', ')}`,
  });

  // 4. Every registered lobby map advertises metadata.lobbyTools.
  //    Documentation-only — server uses registry as source of truth, but
  //    we flag drift so the next dev updates one when changing the other.
  let metadataMatches = 0;
  let metadataDrift = 0;
  const driftDetails: string[] = [];
  const registry = LevelRegistry.getInstance();
  for (const entry of LOBBY_TOOLS_MAP_REGISTRY) {
    const level = registry.getLevel(entry.id) as { metadata?: { lobbyTools?: { enabled?: boolean } } } | undefined;
    if (!level) {
      metadataDrift++;
      driftDetails.push(`${entry.id} not in LevelRegistry`);
      continue;
    }
    const mapEnabled = level.metadata?.lobbyTools?.enabled === true;
    if (mapEnabled === entry.config.enabled) metadataMatches++;
    else {
      metadataDrift++;
      driftDetails.push(`${entry.id}: registry.enabled=${entry.config.enabled} map.metadata=${mapEnabled}`);
    }
  }
  checks.push({
    id: 'lt_metadata_sync',
    label: 'Map metadata ↔ registry agreement',
    status: driftStatus(metadataDrift, /* breaksInvariant */ false),
    detail: metadataDrift === 0
      ? `${metadataMatches}/${LOBBY_TOOLS_MAP_REGISTRY.length} maps agree`
      : `${metadataDrift} drift: ${driftDetails.join('; ')}`,
  });

  // 5. Feature flag is readable.
  checks.push(booleanFlagCheck({
    id: 'lt_feature_flag',
    label: 'DevConfig.lobbyToolsEnabled',
    flagName: 'DevConfig.lobbyToolsEnabled',
    value: DevConfig.lobbyToolsEnabled,
    overrideHint: '?lobbyTools=0|1',
  }));

  // 6. Caps + cooldown are sane.
  checks.push(capsSanityCheck({
    id: 'lt_caps_sane',
    label: 'Cooldown + caps sane',
    caps: {
      cooldownMs: { value: LOBBY_TOOL_COOLDOWN_MS, ok: v => v >= 0 },
      maxMobs: { value: LOBBY_TOOL_MAX_MOBS_PER_PLAYER, ok: v => Number.isInteger(v) && v >= 1 },
      maxBots: { value: LOBBY_TOOL_MAX_BOTS_PER_PLAYER, ok: v => Number.isInteger(v) && v >= 1 },
      spawnOffsetPx: { value: LOBBY_TOOL_SPAWN_OFFSET_PX, ok: v => Number.isFinite(v) },
    },
  }));

  // 7. Bot class constant is set.
  checks.push({
    id: 'lt_bot_class',
    label: 'Default bot class',
    status: typeof LOBBY_TOOL_BOT_CLASS === 'string' && LOBBY_TOOL_BOT_CLASS.length > 0 ? 'pass' : 'fail',
    detail: `${LOBBY_TOOL_BOT_CLASS || '(empty)'}`,
  });

  // 8. Message name matches snake_case convention.
  checks.push(stringPatternCheck({
    id: 'lt_message_name',
    label: 'Colyseus message name',
    value: LOBBY_TOOL_MESSAGE,
    pattern: /^[a-z][a-z0-9_]*$/,
  }));

  // 9. LOBBY_TOOLS enum values are unique.
  checks.push(enumUniqueCheck({
    id: 'lt_enum_unique',
    label: 'LOBBY_TOOLS ids unique',
    record: LOBBY_TOOLS,
  }));

  // 10. getLobbyToolsMapConfig agrees with LOBBY_MAP_IDS.
  const helperAgrees = LOBBY_MAP_IDS.every(id => getLobbyToolsMapConfig(id)?.enabled === true);
  checks.push({
    id: 'lt_helper_consistent',
    label: 'getLobbyToolsMapConfig ↔ LOBBY_MAP_IDS',
    status: helperAgrees ? 'pass' : 'fail',
    detail: helperAgrees ? 'all enabled maps agree' : 'Helper returned disabled/undefined for an enabled map',
  });

  return { id: 'lobby-tools', label: 'Lobby Tools', icon: '🧰', checks };
}
